import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import { Table } from "@tanstack/react-table"

import { Button } from "components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select"

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  totalCount?: number
}

export function DataTablePagination<TData>({
  table,
  totalCount = 0,
}: DataTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const currentPageSize = table.getRowModel().rows.length;
  const startItem = totalCount === 0 ? 0 : (pageIndex * pageSize) + 1;
  const endItem = (pageIndex * pageSize) + currentPageSize;
  
  return (
    <div className="flex flex-row items-center justify-between gap-2 px-1 sm:px-2 lg:px-2 py-0.5 lg:py-0">
      <div className="text-[11px] sm:text-xs lg:text-sm text-muted-foreground truncate">
        {totalCount === 0 ? (
          "No results"
        ) : (
          `Showing ${startItem} to ${endItem} of ${totalCount} results`
        )}
      </div>
      <div className="flex items-center gap-1.5 sm:gap-4 lg:space-x-8 shrink-0">
        <div className="flex items-center space-x-1 sm:space-x-2">
          <p className="text-[11px] sm:text-xs lg:text-sm font-medium text-gray-600 lg:text-gray-900 whitespace-nowrap">
            Rows <span className="hidden sm:inline">per page</span>
          </p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value))
            }}
          >
            <SelectTrigger className="h-7 lg:h-8 w-[58px] lg:w-[70px] px-1.5 lg:px-3 text-[11px] sm:text-xs lg:text-sm">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[11px] sm:text-xs lg:text-sm font-medium text-gray-700 whitespace-nowrap lg:w-[100px] lg:text-center">
          <span className="hidden lg:inline">Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
          <span className="lg:hidden">{table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</span>
        </div>
        <div className="flex items-center space-x-1 lg:space-x-2">
          <Button
            variant="outline"
            className="hidden h-7 w-7 lg:h-8 lg:w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-7 w-7 lg:h-8 lg:w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-7 w-7 lg:h-8 lg:w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-7 w-7 lg:h-8 lg:w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
